import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { Brain, RefreshCw, Settings, Sparkles, FileText, Edit3, RotateCcw } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import AIInsightsTab from './ProfileHub/AIInsightsTab';
import toast from 'react-hot-toast';

const StrategicAssessmentHub = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentUser } = useFamilyContext();

  // Get active view from URL param, default to 'overview'
  const urlView = searchParams.get('view');
  const validViews = ['overview', 'insights', 'revise', 'settings'];
  const initialView = validViews.includes(urlView) ? urlView : 'overview';
  const [activeView, setActiveView] = useState(initialView);

  const [assessmentData, setAssessmentData] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiConfidence, setAiConfidence] = useState(87);

  // Sync activeView with URL parameter
  useEffect(() => {
    const urlView = searchParams.get('view');
    if (urlView && validViews.includes(urlView) && urlView !== activeView) {
      setActiveView(urlView);
    }
  }, [searchParams]);

  useEffect(() => {
    if (currentUser?.id) {
      loadAssessmentData();
    }
  }, [currentUser]);

  const loadAssessmentData = async () => {
    try {
      setLoading(true);

      // Load assessment responses from the correct table
      const { data: assessment, error: assessmentError } = await supabase
        .from('assessment_responses')
        .select('*')
        .eq('user_id', currentUser.id)
        .eq('assessment_type', 'strategic')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (assessmentError) {
        console.error('Error loading assessment:', assessmentError);
      }

      console.log('Loaded assessment data:', assessment);
      setAssessmentData(assessment);

      // Load profile data for AI Insights
      const { data: profile, error: profileError } = await supabase
        .from('student_academic_profiles')
        .select('*')
        .eq('user_id', currentUser.id)
        .single();

      if (profileError && profileError.code !== 'PGRST116') {
        console.error('Error loading profile:', profileError);
      }

      setProfileData(profile);

    } catch (error) {
      console.error('Error loading assessment data:', error);
      toast.error('Failed to load assessment data');
    } finally {
      setLoading(false);
    }
  };

  const handleRetakeAssessment = () => {
    if (confirm('Are you sure you want to retake the entire assessment? This will archive your current responses.')) {
      navigate('/college/assessment');
    }
  };

  const handleReviseAssessment = () => {
    navigate('/college/assessment?mode=revise');
  };

  const handleRegenerateInsights = async () => {
    // TODO: Implement regenerate insights functionality
    toast.success('Regenerating AI insights...');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-stone-600">Loading your strategic assessment...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Overview View */}
        {activeView === 'overview' && (
          <div className="space-y-6">
            {/* Assessment Status Card */}
            <div className="bg-white rounded-xl shadow-sm border border-stone-200 p-6">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
                    <Brain className="w-6 h-6 text-indigo-600" />
                    Your Strategic Assessment
                  </h2>
                  <p className="text-stone-600 mt-1">
                    {assessmentData ? 'View and manage your strategic profile' : 'Get started with your strategic assessment'}
                  </p>
                </div>
                {assessmentData && (
                  <div className="flex items-center gap-2 px-4 py-2 bg-green-50 border border-green-200 rounded-lg">
                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                    <span className="text-sm font-medium text-green-700">Completed</span>
                  </div>
                )}
              </div>

              {assessmentData ? (
                <div className="space-y-4">
                  {/* Assessment Summary */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                      <div className="text-sm text-indigo-600 font-medium">Completed</div>
                      <div className="text-2xl font-semibold text-indigo-900 mt-1">
                        {new Date(assessmentData.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="p-4 bg-teal-50 rounded-lg border border-teal-200">
                      <div className="text-sm text-teal-600 font-medium">AI Confidence</div>
                      <div className="text-2xl font-semibold text-teal-900 mt-1">{aiConfidence}%</div>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                      <div className="text-sm text-purple-600 font-medium">Status</div>
                      <div className="text-lg font-semibold text-purple-900 mt-1">Active</div>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="flex flex-wrap gap-3 pt-4 border-t border-stone-200">
                    {assessmentData?.results_permalink && (
                      <button
                        onClick={() => window.open(assessmentData.results_permalink, '_blank')}
                        className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-indigo-600 text-indigo-600 rounded-lg hover:bg-indigo-50 transition-all"
                      >
                        <FileText className="w-4 h-4" />
                        View Full Results
                      </button>
                    )}
                    <button
                      onClick={handleReviseAssessment}
                      className="flex items-center gap-2 px-4 py-2 bg-white border border-stone-300 text-stone-700 rounded-lg hover:bg-stone-50 transition-all"
                    >
                      <Edit3 className="w-4 h-4" />
                      Revise Responses
                    </button>
                    <button
                      onClick={() => setActiveView('insights')}
                      className="flex items-center gap-2 px-4 py-2 bg-white border border-stone-300 text-stone-700 rounded-lg hover:bg-stone-50 transition-all"
                    >
                      <Sparkles className="w-4 h-4" />
                      View AI Insights
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <Brain className="w-16 h-16 text-stone-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-stone-900 mb-2">
                    No Assessment Completed Yet
                  </h3>
                  <p className="text-stone-600 mb-6">
                    Complete your strategic assessment to unlock personalized insights and recommendations.
                  </p>
                  <button
                    onClick={() => navigate('/college/assessment')}
                    className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all font-medium"
                  >
                    Start Assessment
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* AI Insights View */}
        {activeView === 'insights' && (
          <AIInsightsTab
            profileData={profileData}
            aiConfidence={aiConfidence}
            onRegenerate={handleRegenerateInsights}
          />
        )}

        {/* Revise View */}
        {activeView === 'revise' && (
          <div className="bg-white rounded-xl shadow-sm border border-stone-200 p-6">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">Revise Assessment</h2>
            <p className="text-stone-600 mb-6">
              Update individual responses or sections of your strategic assessment.
            </p>
            <button
              onClick={handleReviseAssessment}
              className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all"
            >
              Open Assessment Editor
            </button>
          </div>
        )}

        {/* Settings View */}
        {activeView === 'settings' && (
          <div className="bg-white rounded-xl shadow-sm border border-stone-200 p-6">
            <h2 className="text-2xl font-semibold text-slate-900 mb-6 flex items-center gap-2">
              <Settings className="w-6 h-6" />
              Assessment Settings
            </h2>

            <div className="space-y-6">
              {/* Retake Assessment */}
              <div className="pb-6 border-b border-stone-200">
                <h3 className="text-lg font-medium text-stone-900 mb-2">Retake Assessment</h3>
                <p className="text-stone-600 mb-4">
                  Start fresh with a new assessment. Your current responses will be archived.
                </p>
                <button
                  onClick={handleRetakeAssessment}
                  className="flex items-center gap-2 px-4 py-2 bg-white border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-all"
                >
                  <RotateCcw className="w-4 h-4" />
                  Retake Assessment
                </button>
              </div>

              {/* Regenerate Insights */}
              <div className="pb-6 border-b border-stone-200">
                <h3 className="text-lg font-medium text-stone-900 mb-2">Regenerate AI Insights</h3>
                <p className="text-stone-600 mb-4">
                  Generate fresh insights based on your current assessment and profile data.
                </p>
                <button
                  onClick={handleRegenerateInsights}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-all"
                >
                  <RefreshCw className="w-4 h-4" />
                  Regenerate Insights
                </button>
              </div>

              {/* Assessment History */}
              <div>
                <h3 className="text-lg font-medium text-stone-900 mb-2">Assessment History</h3>
                <p className="text-stone-600 mb-4">
                  View previous versions of your assessment.
                </p>
                <button
                  disabled
                  className="px-4 py-2 bg-stone-100 text-stone-400 rounded-lg cursor-not-allowed"
                >
                  View History (Coming Soon)
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StrategicAssessmentHub;
